// ============================================================================
// crmAvisados · a fala de cliente que JÁ foi avisada neste navegador (30/10/2026, CRM_VIGIA)
// ============================================================================
// Pedido do dono (30/10/2026): "notificação de mensagem para quando a recepcionista
// estiver no computador e estiver fora do DNA ... elas estão se perdendo. O som também
// não está funcionando." O vigia (VigiaCrm, em todas as telas) e a Caixa do CRM avisam
// a mesma fala por caminhos diferentes; aqui fica o combinado entre eles e entre as abas:
//   { [conversaId]: ultimaDoClienteEmISO } em CHAVE_AVISADOS (crmAvisoContrato.js);
//   · antes de tocar ou avisar, quem vai avisar consulta (naoAvisados) e, avisando,
//     marca (marcarAvisados): a mesma fala nunca toca duas vezes, nem quando a aba da
//     Caixa volta a ficar visível nem depois de recarregar;
//   · a leitura é sempre do armazenamento (outra aba pode ter marcado agora);
//   · podado em AVISADOS_MAX conversas e AVISADOS_TTL_MS (a fala de mais de 24 h já
//     não está numa conversa aberta);
//   · leitura e escrita em try/catch: sem localStorage (aba anônima, bloqueado), vale
//     só a memória desta aba (nunca erro na tela).
// ============================================================================

import { CHAVE_AVISADOS, AVISADOS_TTL_MS, AVISADOS_MAX } from './crmAvisoContrato.js';

const ms = (v) => (v ? Date.parse(v) : Number.NaN);
// sem armazenamento: a memória desta aba (o vigia e a Caixa da mesma aba ainda combinam)
let memoria = {};

function armazem() {
  try { return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null; } catch { return null; }
}

/** Só ISO válido, dentro do TTL e no máximo AVISADOS_MAX (as falas mais novas ficam). */
export function podarAvisados(mapa, agoraMs = Date.now(), { ttl = AVISADOS_TTL_MS, max = AVISADOS_MAX } = {}) {
  const corte = agoraMs - ttl;
  const itens = Object.entries(mapa && typeof mapa === 'object' && !Array.isArray(mapa) ? mapa : {})
    .filter(([k, v]) => k && typeof v === 'string' && Number.isFinite(ms(v)) && ms(v) >= corte)
    .sort((a, b) => ms(b[1]) - ms(a[1]))
    .slice(0, max);
  return Object.fromEntries(itens);
}

/** O mapa de agora (podado). storage null = a memória desta aba. */
export function lerAvisados({ storage = armazem(), agoraMs = Date.now() } = {}) {
  if (!storage) return podarAvisados(memoria, agoraMs);
  try {
    const bruto = storage.getItem(CHAVE_AVISADOS);
    return podarAvisados(bruto ? JSON.parse(bruto) : {}, agoraMs);
  } catch { return podarAvisados(memoria, agoraMs); }
}

function gravar(mapa, storage) {
  memoria = mapa;
  if (!storage) return false;
  try { storage.setItem(CHAVE_AVISADOS, JSON.stringify(mapa)); return true; } catch { return false; }
}

/** Esta fala (a hora da última fala do cliente) já foi avisada? */
export function jaAvisado(mapa, id, em) {
  const a = mapa?.[id];
  return !!a && Number.isFinite(ms(em)) && ms(a) >= ms(em);
}

/** itens [{ id, em }] → só os que ainda não foram avisados. */
export function naoAvisados(itens, { storage = armazem(), agoraMs = Date.now() } = {}) {
  const mapa = lerAvisados({ storage, agoraMs });
  return (itens || []).filter(x => x?.id && Number.isFinite(ms(x.em)) && !jaAvisado(mapa, x.id, x.em));
}

/** Marca itens [{ id, em }] como avisados (relê antes: junta com o que outra aba marcou). → o mapa novo */
export function marcarAvisados(itens, { storage = armazem(), agoraMs = Date.now() } = {}) {
  const mapa = { ...lerAvisados({ storage, agoraMs }) };
  for (const x of itens || []) {
    if (!x?.id || !Number.isFinite(ms(x.em))) continue;
    if (!jaAvisado(mapa, x.id, x.em)) mapa[x.id] = new Date(ms(x.em)).toISOString();
  }
  const novo = podarAvisados(mapa, agoraMs);
  gravar(novo, storage);
  return novo;
}

/**
 * A Caixa: ids que receberam fala nova de cliente no tick + as linhas do tick → os itens [{ id, em }] que
 * ainda não foram avisados. A conversa aberta que não está na lista vem sem a hora: passa (toca como
 * antes) e não é marcada.
 */
export function paraAvisar(ids, linhas, opcoes = {}) {
  const itens = (ids || []).map(id => ({ id, em: (linhas || []).find(x => x?.id === id)?.ultimaDoClienteEm || null }));
  return [...naoAvisados(itens.filter(x => x.em), opcoes), ...itens.filter(x => !x.em)];
}

/** Só para os testes. */
export function _zerarAvisados() { memoria = {}; }
